import { FC, useEffect, useRef, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Col, Flex, Input, InputRef, Modal, Row } from 'antd';
import type { UploadFile } from 'antd';
import { CircleFadingPlus } from 'lucide-react';
import { TbArrowRight, TbCalendarEvent, TbFlag, TbPaperclip, TbProgress } from 'react-icons/tb';
import { toast } from '@/shared/components/toasts/PlatformToast';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useDirtyResourceDraft } from '@/hooks/useResourceDrafts';
import { useSubmitShortcut } from '@/hooks/useSubmitShortcut';
import { CloudStorageConnection, getCloudStorageCapabilities, taskService, uploadCloudAttachment } from '@/services';
import { TaskCreateInput } from '@/services/database/tasks';
import {
    LearnMoreFooter,
    PlatformDatePicker,
    PriorityDropdown,
    RichTextField,
    TaskParticipantPicker,
    TaskStatusDropdown,
} from '@/shared';
import StagedAttachmentDropzone from '@/shared/components/attachments/StagedAttachmentDropzone';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import SelectDataSourceDropdown, {
    CloudStorageConnectionTrigger,
} from '@/shared/components/dropdowns/SelectDataSourceDropdown';
import { useMemberStore } from '@/stores';
import { MemberPermissionType, SetStateMethod, Task, TaskPriority, TaskStatus, TaskVisibility } from '@/types';
import { dayjs, nanoId } from '@/utilities';

import CreateMoreControl from '../components/CreateMoreControl';
import ResourceCreationFormLabel from '../components/ResourceCreationFormLabel';
import ResourceCreationModalContent from '../components/ResourceCreationModalContent';
import ResourceCreationModalTitle from '../components/ResourceCreationModalTitle';

interface CreateTaskModalProps {
    onCreated?: (task: Task) => void;
    open: boolean;
    setOpen: SetStateMethod<boolean>;
}

const statusManagerRoles = new Set([MemberPermissionType.ADMIN, MemberPermissionType.OWNER]);

const CreateTaskModal: FC<CreateTaskModalProps> = ({ onCreated, open, setOpen }): JSX.Element => {
    const { token } = useAntDesignTheme();
    const selectedMember = useMemberStore((state) => state.selectedMember);
    const titleInputRef = useRef<InputRef>(null);
    const [assigneeMemberIds, setAssigneeMemberIds] = useState<string[]>([]);
    const [attachmentConnection, setAttachmentConnection] = useState<Partial<CloudStorageConnection>>({});
    const [attachmentFiles, setAttachmentFiles] = useState<UploadFile[]>([]);
    const [createMoreEnabled, setCreateMoreEnabled] = useState(false);
    const [description, setDescription] = useState('');
    const [dueAt, setDueAt] = useState<Date | null>(null);
    const [loading, setLoading] = useState(false);
    const [priority, setPriority] = useState<TaskPriority | ''>('');
    const [reviewerMemberIds, setReviewerMemberIds] = useState<string[]>([]);
    const [status, setStatus] = useState<TaskStatus | null>(null);
    const [title, setTitle] = useState('');
    const [visibility, setVisibility] = useState<string>(TaskVisibility.PRIVATE);

    const resetForm = () => {
        setAssigneeMemberIds([]);
        setAttachmentFiles([]);
        setDescription('');
        setDueAt(null);
        setPriority('');
        setReviewerMemberIds([]);
        setStatus(null);
        setTitle('');
        setVisibility(TaskVisibility.PRIVATE);
    };
    const { dirtyDraftModal, markSubmitted, requestClose } = useDirtyResourceDraft({
        close: () => setOpen(false),
        isDirty:
            title.trim() !== '' ||
            description !== '' ||
            dueAt !== null ||
            priority !== '' ||
            status !== null ||
            assigneeMemberIds.length > 0 ||
            reviewerMemberIds.length > 0 ||
            attachmentFiles.length > 0,
        reset: resetForm,
        resourceLabel: 'Task',
        resourceType: 'task',
        title,
        values: {
            assigneeMemberIds,
            description,
            dueAt,
            priority,
            reviewerMemberIds,
            statusId: status?.id,
            title,
            visibility,
        },
    });

    useEffect(() => {
        if (!open) {
            return;
        }
        const timeout = window.setTimeout(() => titleInputRef.current?.focus(), 125);

        return () => window.clearTimeout(timeout);
    }, [open]);

    const handleCreateTask = async () => {
        const pendingAttachmentFiles = attachmentFiles.flatMap((file) =>
            file.originFileObj ? [file.originFileObj] : [],
        );
        const attachmentStorage =
            attachmentConnection.id && attachmentConnection.type
                ? {
                      id: attachmentConnection.id,
                      label: attachmentConnection.label,
                      type: attachmentConnection.type,
                  }
                : undefined;

        if (pendingAttachmentFiles.length && !attachmentStorage) {
            toast.error('Select an Attachment Provider');
            return;
        }

        if (attachmentStorage) {
            const { maxFileSizeBytes } = getCloudStorageCapabilities(attachmentStorage);
            const oversizedFile = pendingAttachmentFiles.find((file) => file.size > maxFileSizeBytes);

            if (oversizedFile) {
                toast.error('Attachment is Too Large', {
                    description: `${oversizedFile.name} exceeds this provider's file-size limit.`,
                });
                return;
            }
        }

        const normalizedVisibility =
            Object.values(TaskVisibility).find((visibilityOption) => visibilityOption === visibility) ??
            TaskVisibility.PRIVATE;
        const payload: TaskCreateInput = {
            assigneeMemberIds,
            associations: [],
            description,
            dueAt,
            priority: priority || null,
            reviewerMemberIds,
            statusId: status?.id,
            title: title.trim(),
            visibility: normalizedVisibility,
        };

        try {
            setLoading(true);
            const createdTask = await taskService.create(payload);
            const attachmentUploadResults = attachmentStorage
                ? await Promise.allSettled(
                      pendingAttachmentFiles.map((file) =>
                          uploadCloudAttachment({
                              connection: attachmentStorage,
                              destination: {
                                  listName: 'Tasks',
                                  recordName: createdTask.title,
                                  resourceId: createdTask.id,
                                  resourceName: createdTask.title,
                                  resourceType: 'task',
                              },
                              file,
                              uploadId: nanoId(20),
                          }),
                      ),
                  )
                : [];
            const failedAttachmentUploads = attachmentUploadResults.filter(
                (result): result is PromiseRejectedResult => result.status === 'rejected',
            );

            failedAttachmentUploads.forEach((result) => captureException(result.reason));

            markSubmitted();
            resetForm();
            onCreated?.(createdTask);

            if (!createMoreEnabled) {
                setOpen(false);
            } else {
                window.setTimeout(() => titleInputRef.current?.focus(), 0);
            }

            if (failedAttachmentUploads.length) {
                toast.warning('Task Created with Attachment Errors', {
                    description: `${createdTask.title} was created, but ${failedAttachmentUploads.length.toLocaleString()} ${failedAttachmentUploads.length === 1 ? 'attachment' : 'attachments'} could not be uploaded.`,
                });
            } else {
                toast('Task Created', {
                    description: pendingAttachmentFiles.length
                        ? `${createdTask.title} · ${pendingAttachmentFiles.length.toLocaleString()} ${pendingAttachmentFiles.length === 1 ? 'attachment' : 'attachments'} uploaded`
                        : createdTask.title,
                    icon: <TbProgress size='1rem' />,
                });
            }
        } catch (error) {
            captureException(error);
            toast.error('Unable to Create Task');
        } finally {
            setLoading(false);
        }
    };

    useSubmitShortcut(open && title.trim() !== '' && !loading, handleCreateTask, [
        assigneeMemberIds,
        attachmentConnection,
        attachmentFiles,
        createMoreEnabled,
        description,
        dueAt,
        priority,
        reviewerMemberIds,
        status,
        title,
        visibility,
    ]);

    const canManageStatuses = Boolean(selectedMember?.permission && statusManagerRoles.has(selectedMember.permission));

    return (
        <>
            {dirtyDraftModal}
            <Modal
                destroyOnHidden
                footer={
                    <Row align='middle'>
                        <Col flex='none'>
                            <LearnMoreFooter align='left' topic='Tasks' />
                        </Col>
                        <Col flex='auto' />
                        <Col flex='none'>
                            <CreateMoreControl checked={createMoreEnabled} onChange={setCreateMoreEnabled} />
                            <Button
                                disabled={loading || title.trim() === ''}
                                loading={loading}
                                size='middle'
                                onClick={() => void handleCreateTask()}
                                type='primary'
                            >
                                Create Task
                                <TbArrowRight size='1rem' />
                            </Button>
                        </Col>
                    </Row>
                }
                mask={false}
                onCancel={requestClose}
                open={open}
                styles={{ body: { padding: 0 } }}
                title={
                    <ResourceCreationModalTitle
                        icon={<CircleFadingPlus size='1rem' />}
                        setVisibility={setVisibility}
                        title='Create Task'
                        visibility={visibility}
                    />
                }
                width='52rem'
            >
                <ResourceCreationModalContent>
                    <div
                        style={{
                            borderBottom: `1px solid ${token.colorBorder}`,
                            borderTop: `1px solid ${token.colorBorder}`,
                            marginBottom: '0.5rem',
                            marginTop: '1rem',
                            paddingBottom: '0.5rem',
                            paddingTop: '0.5rem',
                        }}
                    >
                        <Flex
                            align='center'
                            gap='0.5rem'
                            style={{
                                borderBottom: `1px solid ${token.colorBorder}`,
                                overflowX: 'auto',
                                paddingBottom: '0.5rem',
                                whiteSpace: 'nowrap',
                            }}
                            wrap='nowrap'
                        >
                            <Flex align='center' gap='0.5rem' style={{ flex: 'none' }}>
                                <TaskStatusDropdown
                                    manageEnabled={canManageStatuses}
                                    onChange={setStatus}
                                    value={status}
                                />
                                <PriorityDropdown priority={priority} setPriority={setPriority}>
                                    <DropdownButton size='small'>
                                        <TbFlag />
                                        {priority || 'Priority'}
                                    </DropdownButton>
                                </PriorityDropdown>
                                <TaskParticipantPicker
                                    label='Assignees'
                                    onChange={setAssigneeMemberIds}
                                    selectedMemberIds={assigneeMemberIds}
                                    variant='assignee'
                                />
                                <TaskParticipantPicker
                                    label='Reviewers'
                                    onChange={setReviewerMemberIds}
                                    selectedMemberIds={reviewerMemberIds}
                                    variant='reviewer'
                                />
                            </Flex>
                            <span aria-hidden style={{ flex: '1 1 auto', minWidth: '1rem' }} />
                            <PlatformDatePicker
                                allowClear
                                onChange={(value) => setDueAt(value?.toDate() ?? null)}
                                placeholder='Due Date...'
                                size='small'
                                style={{ flex: 'none' }}
                                suffixIcon={<TbCalendarEvent />}
                                value={dueAt ? dayjs(dueAt) : null}
                            />
                        </Flex>
                        <Input
                            bordered={false}
                            maxLength={250}
                            onChange={(event) => setTitle(event.target.value)}
                            placeholder='Task Name...'
                            ref={titleInputRef}
                            size='large'
                            style={{ fontWeight: 500, margin: '0.5rem 0', paddingLeft: 0, paddingRight: 0 }}
                            value={title}
                        />
                        <RichTextField
                            minHeight='12rem'
                            onChange={setDescription}
                            placeholder='Add A Task Description...'
                            value={description}
                        />
                        <section
                            style={{
                                borderTop: `1px solid ${token.colorBorder}`,
                                marginTop: '0.75rem',
                                paddingTop: '0.75rem',
                            }}
                        >
                            <Flex align='center' justify='space-between' style={{ marginBottom: '0.5rem' }}>
                                <Flex align='center' gap='0.375rem'>
                                    <TbPaperclip style={{ color: token.colorTextTertiary }} />
                                    <ResourceCreationFormLabel help='Choose a connected cloud storage provider and add files to upload after the task is created.'>
                                        Attachments (Optional)
                                    </ResourceCreationFormLabel>
                                </Flex>
                                <SelectDataSourceDropdown
                                    disabled={loading}
                                    selectedConnection={attachmentConnection}
                                    setSelectedConnection={setAttachmentConnection}
                                >
                                    <CloudStorageConnectionTrigger
                                        disabled={loading}
                                        selectedConnection={attachmentConnection}
                                    />
                                </SelectDataSourceDropdown>
                            </Flex>
                            <StagedAttachmentDropzone
                                disabled={loading || !attachmentConnection.id || !attachmentConnection.type}
                                files={attachmentFiles}
                                onChange={setAttachmentFiles}
                            />
                        </section>
                    </div>
                </ResourceCreationModalContent>
            </Modal>
        </>
    );
};

export default CreateTaskModal;
